import React, { useContext, useCallback, useRef, useMemo, useState } from 'react';
import { BasicData } from '@App';
import './style.scss';
import OverviewTimelineRow from '../overview-timeline-row';
import { getLetters } from '@utils/Utils';

import { Trans } from '@lingui/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';

const OverviewTimeline = ({ bodyData, filters }) => {
    const { trip_summary, accommodations, destinations } = useContext(BasicData);
    const letterIndexRef = useRef(0);

    const legs = useMemo(() => {
        return trip_summary?.legs?.filter(x => x.type === "Departure" || (x.accommodation_id || x.destination_id || x.name || x.stops)) || [];
    }, [trip_summary]);

    const initialOpenState = useMemo(() => {
        const state = {};
        legs.forEach((leg) => {
            state[leg.id] = true;
        });
        return state;
    }, [legs]);

    const [openLegs, setOpenLegs] = useState(initialOpenState);

    const getLabel = useCallback((start, end, mode, routeLabel) => {
        switch (mode) {
            case 'BoatNoRoute':
            case 'Boat':
                return <div><p><Trans>Boat</Trans> {routeLabel ? <> - <Trans values={routeLabel} id={routeLabel}></Trans> : </> : ""} {start ? <Trans>departs</Trans> : <Trans>arrives at</Trans>} </p><span>{start ? start : end}</span></div>;
            case 'TrainNoRoute':
            case 'Train':
                return <div><p><Trans>Train</Trans> {routeLabel ? <> - <Trans values={routeLabel} id={routeLabel}></Trans> : </> : ""} {start ? <Trans>departs</Trans> : <Trans>arrives at</Trans>} </p><span>{start ? start : end}</span></div>;
            case 'ScheduledFlight':
                return <div><p><Trans>Scheduled flight</Trans> {routeLabel ? <> - <Trans values={routeLabel} id={routeLabel}></Trans> : </> : ""} {start ? <Trans>departs</Trans> : <Trans>arrives at</Trans>}</p> <span>{start ? start : end}</span></div>;
            case 'CharterFlight':
                return <div><p><Trans>Charter flight</Trans> {routeLabel ? <> - <Trans values={routeLabel} id={routeLabel}></Trans> : </> : ""} {start ? <Trans>departs</Trans> : <Trans>arrives at</Trans>}</p> <span>{start ? start : end}</span></div>;
            case 'Selfdrive':
                return <div><p>{start ? <><Trans>Self drive</Trans> {routeLabel ? <> - <Trans values={routeLabel} id={routeLabel}></Trans> : </> : ""} <Trans>from</Trans></> : <Trans>Arrive at</Trans>} </p><span>{start ? start : end}</span></div>;
            case 'Transfer':
            case 'TransferNoRoute':
                return <div><p><Trans>Transfer</Trans> {routeLabel ? <> - <Trans values={routeLabel} id={routeLabel}></Trans> : </> : ""} {start ? <Trans>departs</Trans> : <Trans>arrives at</Trans>}</p> <span>{start ? start : end}</span></div>;
            case 'Hike':
                return <div><p><Trans>Hike</Trans> {routeLabel ? <> - <Trans values={routeLabel} id={routeLabel}></Trans> : </> : ""} {start ? <Trans>departs</Trans> : <Trans>arrives at</Trans>}</p> <span>{start ? start : end}</span></div>;
            default:
                break;
        }
    }, [])

    const getRouteInfoLabel = useCallback((mode, terminal, point) => {
        switch (mode) {
            case 'Boat':
            case 'Train':
            case 'BoatNoRoute':
            case 'TrainNoRoute':
                if (point === "start") {
                    return <><Trans>Depart</Trans>: {terminal}</>;
                } else {
                    return <><Trans>Disembark</Trans>: {terminal}</>;
                }
            case 'ScheduledFlight':
            case 'CharterFlight':
                if (point === "start") {
                    return <><Trans>Depart</Trans>: {terminal}</>;
                } else {
                    return <><Trans>Arrive</Trans>: {terminal}</>;
                }
            case 'Selfdrive':
            case 'Transfer':
            case 'TransferNoRoute':
                if (point === "start") {
                    return <><Trans>Pick up</Trans>: {terminal}</>;
                } else {
                    return <><Trans>Drop off</Trans>: {terminal}</>;
                }
            default:
                break;
        }
    }, [])

    const getCheckLabel = useCallback((ceName, type, subtype = "") => {
        const labels = {
            CheckIn: (
                <div className="ot-route-label">
                    <p>
                        <Trans>Stay at</Trans>
                    </p>
                    <span>{ceName}</span>
                </div>
            ),
            Stay: (
                <div className="ot-route-label">
                    <p>
                        <Trans>Stay at</Trans>
                    </p>
                    <span>{ceName}</span>
                </div>
            ),
            CheckOut: {
                CruiseShip: (
                    <div className="ot-route-label">
                        <p>
                            <Trans>Disembark</Trans>{" "}
                            <span>{ceName}</span>
                        </p>
                    </div>
                ),
                Default: (
                    <div className="ot-route-label">
                        <p>
                            <Trans>Check out</Trans>
                        </p>
                        <span>{ceName}</span>
                    </div>
                ),
            },
        };

        if (type === "CheckOut") {
            return labels.CheckOut[subtype] || labels.CheckOut.Default;
        }

        return labels[type] || <></>;
    }, []);

    const generateRows = useCallback((leg, periods, accommodations, destinations) => {
        const tempRows = [];
        let mobileAccommodationAdded = false;

        let currentLetterIndex = letterIndexRef.current;
        const mobileAccommodation = leg.type === "Mobile" ? accommodations[leg.accommodation_id] : null;


        if (periods) {
            periods.forEach((period) => {
                const periodLength = Math.max(1, period.start_day - period.end_day);
                let pendingCheckoutLeg = leg.type === "Departure" || leg.type === "Standard" || (leg.type === "Mobile" && accommodations[leg.accommodation_id]?.type !== "Vehicle");

                period?.element_clusters?.forEach((elements) => {
                    elements?.forEach((element) => {

                        const currentDate = element.timeline_route?.start_date || period.start_date;
                        const row = { startDate: currentDate, nights: leg.nights };

                        if (!mobileAccommodationAdded) {
                            if (filters?.includes(leg.type) && ["Mobile"].includes(leg.type)) {
                                tempRows.push({
                                    ...row,
                                    type: leg.type,
                                    accommodation: mobileAccommodation.name,
                                    startTime: leg.start_time,
                                    endTime: leg.end_time,
                                    nights: leg.nights
                                });
                            }

                            mobileAccommodationAdded = true;
                        }

                        if (["Stop", "StopArrival", "StopDeparture"].includes(element.type) && filters?.includes(element.type)) {
                            const stopNights = element.stop.departure_day - element.stop.arrival_day;
                            const basisPerStop = leg.basis ? leg.basis.filter(b => {
                                const rowStartDate = new Date(row.startDate);
                                const rowEndDate = new Date(rowStartDate);
                                rowEndDate.setDate(rowEndDate.getDate() + stopNights - 1);

                                const dateFrom = new Date(b.date_from);
                                const dateTo = new Date(b.date_to);

                                const normalizedRowStartDate = new Date(rowStartDate.toLocaleDateString());
                                const normalizedRowEndDate = new Date(rowEndDate.toLocaleDateString());
                                const normalizedDateFrom = new Date(dateFrom.toLocaleDateString());
                                const normalizedDateTo = new Date(dateTo.toLocaleDateString());

                                return normalizedRowStartDate <= normalizedDateTo && normalizedRowEndDate >= normalizedDateFrom;
                            }) : [];

                            const stopRow = {
                                ...row,
                                type: element.type,
                                label: element.stop.content_entity_name,
                                basis: basisPerStop.length > 0 ? [...basisPerStop.map(b => b.room_basis_code)] : (leg.basis ? [...leg.basis.map(b => b.room_basis_code)] : []),
                            };

                            // Add pin letter for stop rows
                            stopRow.pinLetter = getLetters(currentLetterIndex);
                            stopRow.nights = stopNights;
                            currentLetterIndex++;

                            tempRows.push(stopRow);
                        }

                        if (filters?.includes(element.type) && ["Route", "RouteStart", "RouteEnd", "TravelArrangement"].includes(element.type)) {
                            const route = element.timeline_route;
                            const start = route?.start_content_entity_name;
                            const end = route?.end_content_entity_name;
                            const mode = route?.mode;

                            if (route?.mode === "NoTransit") return;

                            const routeData = {
                                ...row,
                                type: element.type,
                                agency: route.agency,
                                startTime: route.start_time,
                                endTime: route.end_time,
                                startLabel: start ? getLabel(start, null, mode, route.label) : "",
                                endLabel: end ? getLabel(null, end, mode, route.label) : "",
                                startTerminal: route.start_terminal ? getRouteInfoLabel(mode, route.start_terminal, "start") : "",
                                endTerminal: route.end_terminal ? getRouteInfoLabel(mode, route.end_terminal, "end") : "",
                                referenceCodes: route.reference_codes,
                                flightNumber: route.flight_number,
                                ticketClass: route.ticket_class,
                                vehicle: route.vehicle,
                                mode,
                            }

                            tempRows.push(routeData);
                        }

                        // Handle Accommodation elements
                        if (filters?.includes(element.type) && ["CheckIn", "Stay", "CheckOut"].includes(element.type)) {
                            const ce = element.content_entity_id ? accommodations[element.content_entity_id] : null
                            const extendedCe = element.content_entity_id ? bodyData?.accommodations[element.content_entity_id] : null

                            const alternatives = [];

                            if (leg.alternate_accommodations) {
                                leg.alternate_accommodations.forEach((alternativeId) => {
                                    alternatives.push(accommodations[alternativeId].name)
                                })
                            }

                            if (ce) {
                                const rowToAdd = {
                                    ...row,
                                    type: element.type,
                                    accommodation: ce.name,
                                    label: getCheckLabel(ce.name, element.type, element.subtype),
                                    basis: leg.basis ? [...leg.basis.map(b => b.room_basis_code)]  : [],
                                    alternatives: alternatives,
                                    checkInTime: leg.check_in_time?.replace('h', ':'),
                                    checkOutTime: leg.check_out_time?.replace('h', ':'),
                                    pendingCheckout: element.type === "CheckOut" && periodLength === 1 && pendingCheckoutLeg,
                                };

                                // Add pin letter for rows with pins
                                if (["Stop", "CheckIn", "Stay"].includes(rowToAdd.type)) {
                                    rowToAdd.pinLetter = getLetters(currentLetterIndex);
                                    currentLetterIndex++;
                                }

                                tempRows.push(rowToAdd);
                            }
                        }
                    });
                });
            });
        }

        if (filters?.includes(leg.type) && ["OvernightTravel", "OwnArrangement"].includes(leg.type)) {
            const specialRow = {
                type: leg.type,
                label: leg.type === "OvernightTravel" ? "Overnight Travel" : "Own Arrangements",
                startDate: leg.start_date,
                nights: leg.nights,
                basis: leg.basis ? [...leg.basis.map(b => b.room_basis_code)]  : [],
            }

            tempRows.push(specialRow);
        }

        letterIndexRef.current = currentLetterIndex;

        return { rows: tempRows };

    }, [getCheckLabel, getLabel, getRouteInfoLabel, filters, bodyData?.accommodations]);

    const generateLegData = useMemo(() => {
        letterIndexRef.current = 0;

        return legs.map((leg) => {
            const defaultPeriods = bodyData?.leg_periods?.[leg.id] || [];
            const periods = defaultPeriods.map((dp) => ({ ...dp, leg_id: leg.id }));
            const { rows } = generateRows(leg, periods, accommodations, destinations);
            return { leg, rows };
        });
    }, [legs, bodyData, accommodations, destinations, generateRows]);


    const toggleLeg = (legId) => {
        setOpenLegs(prev => ({
            ...prev,
            [legId]: !prev[legId]
        }));
    };

    const formatDestinationList = (destinationNames = []) => {
        if (destinationNames.length === 0) return '';
        return destinationNames.join(' • ');
    };

    return (
        <div className='overview-timeline'>
            {generateLegData.map(({ leg, rows }) => {
                const destination = destinations[leg.destination_id];
                const stopDestinationIds = leg.stops?.map(stop => stop.destination_id) ?? [];
                const stopDestinationNames = [...new Set(stopDestinationIds
                    .map(id => destinations[id]?.name)
                    .filter(Boolean))];
                
                const destinationNames = stopDestinationNames.length > 0 ? stopDestinationNames : [destination?.name].filter(Boolean);
                const formattedDestinations = formatDestinationList(destinationNames);

                const isOpen = openLegs[leg.id] === true;
                const hasRows = rows && rows.length > 0;

                return (
                    <div key={leg.id} className="overview-timeline-leg">
                        <div className={`overview-timeline-title${hasRows ? ' has-rows' : ''}`} onClick={hasRows ? () => toggleLeg(leg.id) : undefined}>
                            {
                                leg.start_day !== leg.end_day && ((leg.end_day - leg.start_day) > 1)
                                    ? <p><Trans>Day</Trans> {leg.start_day} - {leg.end_day}
                                        {formattedDestinations && (
                                            <span> {'•'} {formattedDestinations}</span>
                                        )}</p>
                                    : <p><Trans>Day</Trans> {leg.start_day} {formattedDestinations && (
                                        <span> {'•'} {formattedDestinations}</span>
                                    )}</p>
                            }

                            {
                                hasRows && (
                                    isOpen
                                        ? <FontAwesomeIcon icon={faMinus} size="xl" />
                                        : <FontAwesomeIcon icon={faPlus} size="xl" />
                                )
                            }
                        </div>
                        {isOpen && (
                            <OverviewTimelineRow rows={rows} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default OverviewTimeline;